/**
 * Drawer — the reusable slide-over modal panel (E10-F02-T06 / T07).
 *
 * The Company Research → Review screen (E07) opens an evidence-drawer: a panel
 * that slides in from the screen edge to show the sources/evidence behind a
 * scored row, over a dimming scrim. In the legacy front-end this drawer had NO
 * focus-trap and NO return-focus — Esc closed it, but Tab leaked to the page
 * behind it and focus was never restored to the row that opened it. This
 * primitive fixes that gap by building on the SAME `modal-layer` mechanics the
 * Dialog uses, so both overlays honour one a11y contract:
 *
 *   - rendered only while `open` (mount/unmount = open/close);
 *   - on open focus moves INTO the panel; Tab / Shift+Tab are trapped inside;
 *   - Esc closes (unless suppressed); a scrim click light-dismisses;
 *   - on close focus returns to the element that opened the drawer;
 *   - `role="dialog"` + `aria-modal="true"` on the panel, and the rest of the
 *     page is marked `aria-hidden`/`inert` while open (background blocked).
 *
 * It is a presentational shell: it owns chrome (slide direction, header with a
 * close cross, scrollable body) + the focus/keyboard mechanics. Feature drawers
 * (the evidence-drawer, etc.) compose it and render their content inside.
 *
 * Colours go through the brand token utilities (`bg-primary`, `text-white`, …)
 * mapped in styles/index.css @theme — never literal hexes (design.md "Don't").
 */
import { useId, type ReactNode } from 'react';
import { ModalPortal, useModalLayer, useScrimDismiss } from './modal-layer.js';

/** Which edge the drawer slides in from. */
export type DrawerSide = 'right' | 'left';

/** Panel width. Maps to a Tailwind `max-w-*` utility (it spans the side fully). */
export type DrawerWidth = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const WIDTH_CLASS: Record<DrawerWidth, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
};

const SIDE_CLASS: Record<DrawerSide, string> = {
  right: 'ml-auto',
  left: 'mr-auto',
};

export interface DrawerProps {
  /** Whether the drawer is shown. When false the drawer is not rendered at all. */
  readonly open: boolean;
  /**
   * Close request — fired by Esc, the close cross, or a scrim click. The parent
   * owns `open`, so this is where it flips it to false.
   */
  readonly onClose: () => void;
  /**
   * Heading text. When provided the navy title bar (with the close cross) is
   * rendered. Required for the labelled a11y heading; if you need a headless
   * drawer pass `ariaLabel` instead.
   */
  readonly title?: ReactNode;
  /**
   * Optional secondary line rendered INSIDE the navy header, under the title
   * (e.g. the evidence-drawer's "<entity> · <grade> · score <n>"). Only shown
   * when `title` is set; wired to the panel via `aria-describedby`.
   */
  readonly subtitle?: ReactNode;
  /** Drawer body content. */
  readonly children: ReactNode;
  /**
   * Optional footer node — rendered in a top-bordered strip at the bottom of
   * the panel (e.g. a Publish/Close action for the evidence-drawer).
   */
  readonly footer?: ReactNode;
  /** Which edge to slide in from. Default `right` (the evidence-drawer side). */
  readonly side?: DrawerSide;
  /** Panel width. Default `lg`. */
  readonly width?: DrawerWidth;
  /** Disable Esc-to-close. Default false (Esc closes). */
  readonly disableEscapeClose?: boolean;
  /** Disable scrim-click-to-close (light dismiss). Default false (click closes). */
  readonly disableBackdropClose?: boolean;
  /** Test id on the panel (also used to derive child test ids). */
  readonly testId?: string;
  /** Extra classes appended to the drawer panel. */
  readonly className?: string;
  /** Accessible label when there is no visible `title` (headless variant). */
  readonly ariaLabel?: string;
}

export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  side = 'right',
  width = 'lg',
  disableEscapeClose = false,
  disableBackdropClose = false,
  testId = 'drawer',
  className,
  ariaLabel,
}: DrawerProps): React.JSX.Element | null {
  const titleId = useId();
  const subtitleId = useId();
  const hasHeader = title !== undefined && title !== null;
  const hasSubtitle = hasHeader && subtitle !== undefined && subtitle !== null;

  // Focus-trap + return-focus + Esc + background-block — shared with Dialog.
  const { panelRef, handleKeyDown } = useModalLayer<HTMLDivElement>({
    open,
    onClose,
    disableEscapeClose,
  });

  // Scrim light-dismiss (shared factory).
  const handleBackdropMouseDown = useScrimDismiss<HTMLDivElement>(onClose, disableBackdropClose);

  if (!open) return null;

  // Portalled to <body> so the background-block can inert the real page root
  // (#root) rather than #root's non-existent app-content siblings — see
  // modal-layer.ts.
  return (
    <ModalPortal>
      <div
        data-testid={`${testId}-backdrop`}
        onMouseDown={handleBackdropMouseDown}
        className="fixed inset-0 z-50 flex bg-gray-900/50"
      >
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={hasHeader ? titleId : undefined}
          aria-label={hasHeader ? undefined : ariaLabel}
          aria-describedby={hasSubtitle ? subtitleId : undefined}
          data-testid={testId}
          data-side={side}
          tabIndex={-1}
          onKeyDown={handleKeyDown}
          // `eer-drawer` owns the transform-based slide (styles/index.css): the
          // panel animates in from its edge; the `.closed` modifier parks it
          // off-screen (`translateX(100%)` on the right side / `-100%` on the
          // left) for the closing/hidden position. `shadow-2xl` per the design
          // spec for the slide-over (heavier than the centred Dialog's xl).
          className={`eer-drawer ${side === 'left' ? 'eer-drawer-left' : ''} flex h-full w-full ${WIDTH_CLASS[width]} ${SIDE_CLASS[side]} flex-col overflow-hidden bg-white shadow-2xl focus:outline-none ${className ?? ''}`}
        >
          {hasHeader ? (
            <header className="flex items-center justify-between gap-4 bg-primary px-6 py-4 text-white">
              <div className="min-w-0">
                <h2
                  id={titleId}
                  data-testid={`${testId}-title`}
                  className="brand-font truncate text-lg font-semibold text-white"
                >
                  {title}
                </h2>
                {hasSubtitle ? (
                  <div
                    id={subtitleId}
                    data-testid={`${testId}-subtitle`}
                    className="mt-0.5 text-xs text-white/70"
                  >
                    {subtitle}
                  </div>
                ) : null}
              </div>
              <button
                type="button"
                data-testid={`${testId}-close`}
                aria-label="Close"
                onClick={onClose}
                className="-mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded text-white/80 hover:bg-primary-hover hover:text-white"
              >
                <i className="fa-solid fa-times" aria-hidden />
              </button>
            </header>
          ) : null}

          <div
            data-testid={`${testId}-body`}
            className="flex-1 overflow-y-auto px-6 py-5 text-text"
          >
            {children}
          </div>

          {footer ? (
            <footer
              data-testid={`${testId}-footer`}
              className="flex items-center justify-end gap-3 border-t border-border px-6 py-4"
            >
              {footer}
            </footer>
          ) : null}
        </div>
      </div>
    </ModalPortal>
  );
}
